import {
  MARK_ARC_FILLED_D,
  MARK_DOT,
  MARK_VIEWBOX,
  WORDMARK_D,
  WORDMARK_VIEWBOX,
  BRAND,
} from "@/lib/brand-assets";

interface BrandLockupProps {
  /** Wordmark height in px (mark scales to match). Default 16. */
  size?: number;
  /** "light" = ink wordmark for cream/white surfaces; "dark" = cream for navy. */
  variant?: "light" | "dark";
  /** Render the icon mark beside the wordmark. Default true. */
  withMark?: boolean;
  className?: string;
}

/**
 * The official lock-up: mark + outlined Poppins Bold wordmark (never retyped).
 * Brand Guidelines v2.0 — always use this instead of typing "CoverMyClass".
 */
export function BrandLockup({ size = 16, variant = "light", withMark = true, className }: BrandLockupProps) {
  const wordFill = variant === "dark" ? BRAND.cream : BRAND.ink;
  const markSize = size * 2.1;
  return (
    <span
      role="img"
      aria-label="Cover My Class"
      className={className}
      style={{ display: "inline-flex", alignItems: "center", gap: size * 0.55 }}
    >
      {withMark && (
        <svg viewBox={MARK_VIEWBOX} style={{ width: markSize, height: markSize * (49 / 48) }} aria-hidden="true">
          <path d={MARK_ARC_FILLED_D} fill={variant === "dark" ? BRAND.cream : BRAND.navy700} />
          <circle cx={MARK_DOT.cx} cy={MARK_DOT.cy} r={MARK_DOT.r} fill={BRAND.navy500} />
        </svg>
      )}
      <svg viewBox={WORDMARK_VIEWBOX} style={{ height: size, width: "auto" }} aria-hidden="true">
        <path d={WORDMARK_D} fill={wordFill} />
      </svg>
    </span>
  );
}

interface BrandMarkProps {
  /** Mark height in px. Default 28. */
  size?: number;
  /** "light" = navy mark for cream/white surfaces; "dark" = cream for navy. */
  variant?: "light" | "dark";
  className?: string;
}

/**
 * The mark on its own, for places that already name the product in words and
 * would only repeat themselves by showing the wordmark too. Same path data as
 * the lock-up, so the two can never drift apart.
 */
export function BrandMark({ size = 28, variant = "light", className }: BrandMarkProps) {
  return (
    <svg
      viewBox={MARK_VIEWBOX}
      role="img"
      aria-label="Cover My Class"
      className={className}
      style={{ width: size, height: size * (49 / 48) }}
    >
      <path d={MARK_ARC_FILLED_D} fill={variant === "dark" ? BRAND.cream : BRAND.navy700} />
      <circle cx={MARK_DOT.cx} cy={MARK_DOT.cy} r={MARK_DOT.r} fill={BRAND.navy500} />
    </svg>
  );
}
